const BLOCKED_TRACKING_HOSTS = ["localhost", "127.0.0.1", "staging.inciteai.com"];
const GOOGLE_ADS_CONVERSION_ID = "11333696455";
const GOOGLE_ADS_SIGN_UP_LABEL = "CPI2CKOAj5wcEMf3qZwq";
const GOOGLE_ADS_PURCHASE_LABEL = "kTJqCKCAj5wcEMf3qZwq";
const X_SIGN_UP_EVENT_ID = "tw-ohy5t-rc4q7";
const BASE_ATTRIBUTION_PARAM_NAMES = [
  "twclid",
  "gclid",
  "gbraid",
  "wbraid",
  "fbclid",
  "msclkid",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
];
const ATTRIBUTION_PARAM_PREFIXES = ["utm_", "ic_"];
const ATTRIBUTION_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 90;

const isBlockedTrackingHost = () => {
  if (typeof window === "undefined") {
    return true;
  }

  return BLOCKED_TRACKING_HOSTS.includes(window.location.hostname);
};

const dataLayerAvailable = () =>
  typeof window !== "undefined" && Array.isArray(window.dataLayer);

const facebookPixelAvailable = () =>
  typeof window !== "undefined" && typeof window.fbq === "function";

const googleAdsAvailable = () =>
  typeof window !== "undefined" && typeof window.gtag === "function";

const xPixelAvailable = () =>
  typeof window !== "undefined" && typeof window.twq === "function";

const trackedEventStorageKey = (dedupeKey) => `incite_tracked_event:${dedupeKey}`;
const attributionStorageKey = (touch) => `incite_attrib:${touch}`;

const filteredAttributionParams = (search) => {
  const params = new URLSearchParams(search || "");
  const out = {};

  params.forEach((value, key) => {
    const normalizedKey = key.toLowerCase();
    const shouldCapture =
      BASE_ATTRIBUTION_PARAM_NAMES.includes(normalizedKey) ||
      ATTRIBUTION_PARAM_PREFIXES.some((prefix) => normalizedKey.startsWith(prefix));

    if (shouldCapture && value && !out[normalizedKey]) {
      out[normalizedKey] = value;
    }
  });

  return out;
};

const hasAttributionParams = (params) => Object.keys(params || {}).length > 0;

const cookieDomain = () => {
  const hostname = window.location.hostname;
  if (hostname === "inciteai.com" || hostname.endsWith(".inciteai.com")) {
    return "Domain=.inciteai.com";
  }
  return null;
};

const writeAttributionCookie = (name, value) => {
  try {
    const parts = [
      `${name}=${encodeURIComponent(JSON.stringify(value))}`,
      "Path=/",
      `Max-Age=${ATTRIBUTION_COOKIE_MAX_AGE_SECONDS}`,
      "SameSite=Lax",
    ];
    if (window.location.protocol === "https:") {
      parts.push("Secure");
    }
    const domain = cookieDomain();
    if (domain) {
      parts.push(domain);
    }
    document.cookie = parts.join("; ");
  } catch (_error) {
    return;
  }
};

const readStoredAttribution = (touch = "last_touch") => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(attributionStorageKey(touch));
    return raw ? JSON.parse(raw) : null;
  } catch (_error) {
    return null;
  }
};

const writeStoredAttribution = (touch, payload) => {
  try {
    window.localStorage.setItem(attributionStorageKey(touch), JSON.stringify(payload));
  } catch (_error) {
    return;
  }
};

export const captureAttributionFromUrl = () => {
  if (typeof window === "undefined") {
    return null;
  }

  const params = filteredAttributionParams(window.location.search);
  if (!hasAttributionParams(params)) {
    return readStoredAttribution();
  }

  const payload = {
    params,
    landing_path: `${window.location.pathname}${window.location.search}`,
    landing_url: window.location.href.slice(0, 1000),
    referrer: document.referrer ? document.referrer.slice(0, 1000) : undefined,
    captured_at: new Date().toISOString(),
  };

  if (!readStoredAttribution("first_touch")) {
    writeStoredAttribution("first_touch", payload);
    writeAttributionCookie("incite_attrib_ft", payload);
  }

  writeStoredAttribution("last_touch", payload);
  writeAttributionCookie("incite_attrib_lt", payload);

  return payload;
};

const storedTwclid = () =>
  readStoredAttribution("last_touch")?.params?.twclid ||
  readStoredAttribution("first_touch")?.params?.twclid;

export const hasTrackedEvent = (dedupeKey) => {
  if (!dedupeKey || typeof window === "undefined") {
    return false;
  }

  try {
    return window.localStorage.getItem(trackedEventStorageKey(dedupeKey)) === "1";
  } catch (_) {
    return false;
  }
};

const markTrackedEvent = (dedupeKey) => {
  if (!dedupeKey || typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(trackedEventStorageKey(dedupeKey), "1");
  } catch (_error) {
    return;
  }
};

const normalizeTrackingEvents = (rawEvents) => {
  if (!rawEvents) {
    return [];
  }

  return Array.isArray(rawEvents) ? rawEvents : [rawEvents];
};

const sendToValue = (label) => `AW-${GOOGLE_ADS_CONVERSION_ID}/${label}`;

const trackGoogleAdsConversion = (payload) => {
  if (!googleAdsAvailable()) {
    return false;
  }

  if (payload.event === "sign_up") {
    window.gtag("event", "conversion", {
      send_to: sendToValue(GOOGLE_ADS_SIGN_UP_LABEL),
    });
    return true;
  }

  if (payload.event === "purchase") {
    window.gtag("event", "conversion", {
      send_to: sendToValue(GOOGLE_ADS_PURCHASE_LABEL),
      value: payload.value,
      currency: payload.currency,
      transaction_id: payload.transaction_id,
      new_customer: true,
    });
    return true;
  }

  return false;
};

const trackXConversion = (payload, dedupeKey) => {
  if (!xPixelAvailable()) {
    return false;
  }

  if (payload.event === "sign_up") {
    const params = {
      status: "completed",
    };
    if (dedupeKey) {
      params.conversion_id = dedupeKey;
    }
    const twclid = payload.twclid || storedTwclid();
    if (twclid) {
      params.twclid = twclid;
    }

    window.twq("event", X_SIGN_UP_EVENT_ID, params);
    return true;
  }

  return false;
};

export const trackBackendEvents = (rawEvents) => {
  if (isBlockedTrackingHost()) {
    return;
  }

  captureAttributionFromUrl();

  const events = normalizeTrackingEvents(rawEvents);

  events.forEach((eventPayload) => {
    if (!eventPayload?.event) {
      return;
    }

    const dedupeKey = eventPayload.dedupe_key;
    if (dedupeKey && hasTrackedEvent(dedupeKey)) {
      return;
    }

    const payload = { ...eventPayload };
    delete payload.dedupe_key;

    if (payload.ecommerce && dataLayerAvailable()) {
      window.dataLayer.push({ ecommerce: null });
    }

    if (dataLayerAvailable()) {
      window.dataLayer.push(payload);
    }

    if (payload.event === "sign_up" && facebookPixelAvailable()) {
      window.fbq("track", "SignUp");
    }

    const googleAdsTracked = trackGoogleAdsConversion(payload);
    const xTracked = trackXConversion(payload, dedupeKey);

    if (dedupeKey && (dataLayerAvailable() || facebookPixelAvailable() || googleAdsTracked || xTracked)) {
      markTrackedEvent(dedupeKey);
    }
  });
};

export const trackFrontendEvent = (event, metadata = {}) => {
  if (!event || isBlockedTrackingHost()) {
    return;
  }

  captureAttributionFromUrl();

  if (dataLayerAvailable()) {
    window.dataLayer.push({
      event,
      ...metadata,
    });
  }
};
